/**
 * Generate Jig context-loading module.
 * Fetches tenant, website, and reference-asset context so prompt generation can be
 * flavored with the most relevant instructions and supporting summaries.
 */
import { AppConst } from '@/constants'
import type { LibraryEntry, LibraryReferenceAsset } from '@/store/modules/teamlibrary/types'
import axios from 'axios'
import type {
  AssetReferencesResponse,
  ContextResult,
  GenerateJigContext,
  TenantContextResponse,
} from './generate-jig.types'
import { isRequestCanceled } from './generate-jig.utils'

declare var consoleError: any
declare var consoleLog: any
declare var consoleWarn: any

function resolveTrustedContextUrl(ctx: GenerateJigContext, url: string): string | null {
  try {
    const apiOrigin = new URL(ctx.apiDomain).origin
    const resolvedUrl = new URL(url, ctx.apiDomain)

    if (resolvedUrl.origin !== apiOrigin) {
      consoleWarn('Blocked external context URL (origin: ' + resolvedUrl.origin + ')')
      return null
    }

    return resolvedUrl.toString()
  } catch (_error) {
    consoleError('Invalid context URL supplied by backend')
    return null
  }
}

function extractContextField(contextData: any, field: string): string | null {
  if (contextData == null) {
    return null
  }
  if (typeof contextData === 'string') {
    try {
      const parsed = JSON.parse(contextData)
      return parsed && parsed[field] != null ? String(parsed[field]) : null
    } catch (_e) {
      return null
    }
  }
  return contextData[field] != null ? String(contextData[field]) : null
}

async function fetchContextContent(ctx: GenerateJigContext, url: string, headers: Record<string, string>): Promise<any> {
  try {
    const fullUrl = resolveTrustedContextUrl(ctx, url)
    if (!fullUrl) {
      return null
    }
    const response = await axios.get(fullUrl, ctx.getRequestConfig({ headers: headers }))
    if (response && response.data != null) {
      return response.data
    }
    return null
  } catch (error) {
    if (isRequestCanceled(error)) {
      throw error
    }
    consoleError('Failed to fetch context from ' + url + ': ' + String(error))
    return null
  }
}

async function resolveContextUrl(
  ctx: GenerateJigContext,
  ctxUrl: string | null | undefined,
  ctxId: number | null | undefined,
  headers: Record<string, string>
): Promise<any> {
  if (ctxUrl) {
    return fetchContextContent(ctx, ctxUrl, headers)
  }
  if (ctxId != null && ctxId > 0) {
    return fetchContextContent(
      ctx,
      ctx.apiDomain + AppConst.APIEndpoint.senseV1Url + AppConst.APIEndpoint.contextUrl + '/' + ctxId,
      headers
    )
  }
  return null
}

async function downloadPresignedContent(ctx: GenerateJigContext, url: string): Promise<{ contentType: string; text: string } | null> {
  try {
    const response = await axios.get(url, { responseType: 'arraybuffer' })
    if (!response || response.data == null) {
      return null
    }
    const rawHeaders = response.headers || {}
    const contentType = typeof rawHeaders['content-type'] === 'string'
      ? rawHeaders['content-type']
      : 'unknown'
    const buffer: ArrayBuffer = response.data

    // For PDF or other binary types, return a placeholder with size info
    if (contentType.indexOf('application/pdf') >= 0) {
      return { contentType: contentType, text: '[PDF binary, ' + buffer.byteLength + ' bytes]' }
    }

    // Try to decode as UTF-8 text
    let text: string
    if (typeof Buffer !== 'undefined') {
      text = Buffer.from(buffer).toString('utf-8')
    } else if (typeof TextDecoder !== 'undefined') {
      text = new TextDecoder('utf-8').decode(buffer)
    } else {
      // Fallback: convert in chunks to avoid stack overflow on large buffers
      const bytes = new Uint8Array(buffer)
      const chunkSize = 8192
      const parts: string[] = []
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length))
        parts.push(String.fromCharCode.apply(null, chunk as any))
      }
      text = parts.join('')
    }

    return { contentType: contentType, text: text }
  } catch (error) {
    if (isRequestCanceled(error)) {
      throw error
    }
    consoleError('Failed to download presigned content from URL: ' + String(error))
    return null
  }
}

async function resolveLibraryRefSummary(
  ctx: GenerateJigContext,
  rawCtx: any,
  ref: LibraryReferenceAsset
): Promise<{ assetGuid: string; filename: string; summary: string | null }> {
  const result = {
    assetGuid: ref.assetGuid || '',
    filename: ref.originalFilename || '',
    summary: null as string | null,
  }

  // Step 3: use inline summary from ctxUrl response to avoid an extra S3 download
  if (rawCtx && typeof rawCtx.summary === 'string' && rawCtx.summary.length > 0) {
    if (process.env.VUE_APP_DEV_TOOLS === 'true') {
      consoleLog('Library ref summary [' + result.filename + ']: using inline summary')
    }
    result.summary = rawCtx.summary
    return result
  }

  // Step 4: fall back to downloading from summary_download_url (presigned S3)
  const summaryUrl = rawCtx && typeof rawCtx.summary_download_url === 'string' ? rawCtx.summary_download_url : null
  if (!summaryUrl) {
    return result
  }

  const downloaded = await downloadPresignedContent(ctx, summaryUrl)
  if (!downloaded) {
    return result
  }

  // Support text content types and direct strings
  if (downloaded.contentType.indexOf('text/') >= 0 || downloaded.contentType.indexOf('application/json') >= 0) {
    let summaryText = downloaded.text
    // If content is a JSON array, join elements into a single string
    try {
      const parsed = JSON.parse(summaryText)
      if (Array.isArray(parsed)) {
        summaryText = parsed.map(function (item: any) { return String(item) }).join('\n')
      }
    } catch (_e) {
      // Not JSON — use raw text as-is
    }
    if (process.env.VUE_APP_DEV_TOOLS === 'true') {
      consoleLog('Library ref summary [' + result.filename + ']: ' + summaryText)
    }
    result.summary = summaryText
  } else {
    consoleWarn('Library ref summary [' + result.filename + ']: unsupported content type "' + downloaded.contentType + '"')
  }

  return result
}

/**
 * Fetches tenant context data needed for Jig generation:
 * 1. Tenant details with context (instructions + website URLs)
 * 2. Tenant asset references with their context URLs
 * 3. Library model asset references with their context URLs
 * All resolved context URLs are fetched in parallel.
 */
export async function fetchContext(
  ctx: GenerateJigContext,
  libraryEntry: LibraryEntry,
  libraryModelReferences: LibraryReferenceAsset[]
): Promise<ContextResult> {
  const headers = ctx.buildAuthHeaders(false)
  const tenantId = ctx.tenantId

  // Step 1: Fetch tenant details and tenant references in parallel.
  const [tenantResponse, tenantRefsResponse] = await Promise.all([
    axios.get<TenantContextResponse>(
      ctx.apiDomain + '/tenants/v1/' + tenantId,
      ctx.getRequestConfig({ params: { include: 'context' }, headers: headers })
    ),
    axios.get<AssetReferencesResponse>(
      ctx.apiDomain + '/asset/v1/references',
      ctx.getRequestConfig({ params: { tenantId: tenantId, page: 1, limit: 999 }, headers: headers })
    ),
  ])

  const tenantData = tenantResponse && tenantResponse.data ? tenantResponse.data : {}
  const context = tenantData.Context || {}
  const websiteUrls = (context.WebsiteUrls || [])
  // v1: tenant reference assets are fetched but not resolved — see commented-out section 2c
  const tenantRefsData = tenantRefsResponse && tenantRefsResponse.data ? tenantRefsResponse.data : {}
  const _tenantRefs = tenantRefsData.references || [] // eslint-disable-line @typescript-eslint/no-unused-vars
  const libraryRefs = libraryModelReferences || []

  // Step 2: Build parallel requests for active context URLs

  // 2a: Instructions context — extract "verbatim" from response
  const instructionsPromise = resolveContextUrl(
    ctx,
    context.InstructionsCtxUrl,
    context.InstructionsCtxId,
    headers
  )

  // 2b: Website URL contexts — extract "summary" from each response
  const websiteEntries = websiteUrls.map(function (entry: any) {
    return {
      url: entry.Url || '',
      ctxUrl: entry.CtxUrl || null,
      contextId: entry.ContextId || null,
    }
  })

  const websiteContextPromises = websiteEntries.map(
    (entry: { url: string; ctxUrl: string | null; contextId: number | null }) =>
      resolveContextUrl(ctx, entry.ctxUrl, entry.contextId, headers)
        .then(function (rawCtx: any) { return { url: entry.url, rawContext: rawCtx } })
  )

  // v1: tenant reference asset contexts are not used in the prompt payload yet.
  // When ready, GET each ref's ctxUrl, then download from summary_download_url.
  // const tenantRefsWithCtx = tenantRefs.filter(function (ref: any) { return ref && ref.ctxUrl })
  // const tenantRefContextPromises = tenantRefsWithCtx.map(
  //   (ref: any) =>
  //     fetchContextContent(ctx, ref.ctxUrl, headers)
  //       .then((ctx: any) => ({
  //         assetGuid: String(ref.assetGuid || ''),
  //         filename: String(ref.originalFilename || ''),
  //         summary: extractContextField(ctx, 'summary'),
  //         summaryDownloadUrl: ctx && typeof ctx.summary_download_url === 'string' ? ctx.summary_download_url : null,
  //         verbatimDownloadUrl: ctx && typeof ctx.verbatim_download_url === 'string' ? ctx.verbatim_download_url : null,
  //       }))
  // )

  // 2d: Library entry fields summary context — extract "verbatim"
  const entryContext = libraryEntry.Context || {}
  const libraryFieldsPromise = resolveContextUrl(
    ctx,
    entryContext.FieldsSummaryCtxURL,
    entryContext.FieldsSummaryCtxID,
    headers
  )

  // 2e: Library entry website context — extract "summary"
  const libraryWebsitePromise = resolveContextUrl(
    ctx,
    entryContext.WebsiteCtxURL,
    entryContext.WebsiteCtxID,
    headers
  )

  // 2f: Library model reference asset contexts
  //
  // Resolution strategy (to minimise unnecessary requests):
  //   1. Skip refs with authoritative contextStatus 'failed' or 'pending' — no summary will be available yet,
  //      so avoid the ctxUrl GET entirely. contextStatus may be set by LibraryEntry.vue when it checks
  //      processing status on page load.
  //   2. When contextStatus is undefined, proceed normally. That can mean either:
  //        - status has not been checked yet (e.g. Library view flow), or
  //        - a previous status lookup failed transiently and we deliberately did not persist 'failed'.
  //   3. GET ctxUrl for eligible refs.
  //   4. If the ctxUrl response contains an inline 'summary' field, use it directly
  //      (avoids the extra S3 download).
  //   5. Otherwise fall back to downloading from 'summary_download_url' (presigned S3 URL).
  const libraryRefsWithCtx = libraryRefs.filter(function (ref: LibraryReferenceAsset) {
    if (!ref || !ref.ctxUrl) {
      return false
    }
    var status = ref.contextStatus
    return status !== 'failed' && status !== 'pending'
  })
  const libraryRefContextPromises = libraryRefsWithCtx.map(
    (ref: LibraryReferenceAsset) =>
      fetchContextContent(ctx, ref.ctxUrl as string, headers)
        .then((rawCtx: any) => resolveLibraryRefSummary(ctx, rawCtx, ref))
  )

  // Step 3: Execute active context fetches in parallel
  // v1 context used in /ai/complete payload:
  //   - TenantName (from tenant API)
  //   - Context.TenantInstruction.Verbatim (instructions context)
  //   - TenantWebURLContextSummary (website URL context summaries)
  //   - LibraryItemURLReferenceSummary (library entry website context)
  //   - LibraryItemDisplayName, LibraryItemDescription, libraryItemFilename (library entry fields)
  // Not yet used: tenant reference asset contexts
  const websiteCount = websiteContextPromises.length
  const allResults = await Promise.all([
    instructionsPromise,
    libraryFieldsPromise,
    libraryWebsitePromise,
    ...websiteContextPromises,
    ...libraryRefContextPromises,
  ])

  // Step 4: Extract specific fields from context responses
  const instructionsRaw = allResults[0]
  const libraryFieldsRaw = allResults[1]
  const libraryWebsiteRaw = allResults[2]
  const websiteResults = allResults.slice(3, 3 + websiteCount) as Array<{ url: string; rawContext: any }>
  const libraryRefResults = allResults.slice(3 + websiteCount) as Array<{ assetGuid: string; filename: string; summary: string | null }>

  const instructionsVerbatim = extractContextField(instructionsRaw, 'verbatim')
  const libraryEntryFieldsSummary = extractContextField(libraryFieldsRaw, 'verbatim')
  const libraryEntryWebsiteSummary = extractContextField(libraryWebsiteRaw, 'summary')
  const websiteSummaries = websiteResults.map(
    (entry) => ({
      url: entry.url,
      summary: extractContextField(entry.rawContext, 'summary'),
    })
  )

  return {
    tenant: {
      tenantName: tenantData.Name != null ? String(tenantData.Name) : null,
      instructionsVerbatim: instructionsVerbatim,
      websiteSummaries: websiteSummaries,
      referenceContexts: [],
    },
    library: {
      entryFieldsSummary: libraryEntryFieldsSummary,
      entryWebsiteSummary: libraryEntryWebsiteSummary,
      modelReferenceContexts: libraryRefResults,
    },
  }
}
